// 面板中的素材库（保留右下角添加素材按钮）
import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft } from "lucide-react";
import { Image } from "@/components/common/image";
import { TruncatedText } from "@/components/common/truncated-text";
import { useTranslation } from "react-i18next";
import { MaterialEmptyImage } from "../agent-dashboard-user-detail/assets";
import { cn } from "@/lib/utils";
import { ModalCloseButton } from "../common/modal-close-button";
import { ItemDetailData, knowledgeGetFolderItems } from "@/services/knowledge-get-folder-items";
import { KnowledgeModalDetail } from "../knowledge-model-detail";
import { agentDashboardKnowledgeHooks } from "@/manager";
import knowledgeBg from '@/assets/assets/knowledge-bg.webp';
import { MinusCircleIcon, PlusCircleIcon } from "../assets-material/assets";
import { KnowledgeChooseModal } from "./knowledge-choose-modal";
import { KnowledgeEmptyImage } from "../assets-knowledge/assets";

type KnowledgeItem = {
  id: number;
  folderName: string;
  folderId: number;
  createTime: string;
}

export const PanelAgentKnowledgeLibrary: React.FC<{
  open: boolean;
  knowledge?: KnowledgeItem[];
  saveLoading?: boolean;
  onAddKnowledgeFolders?: (ids: number[]) => Promise<void>;
  onRemoveKnowledgeFolders?: (ids: number[]) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onAddKnowledge?: (params: { removeIds: number[]; addIds: number[] }) => Promise<void>;
}> = ({
  open,
  onOpenChange,
  knowledge = [],
  saveLoading,
  onAddKnowledge,
  onAddKnowledgeFolders,
  onRemoveKnowledgeFolders
}) => {
    const { t } = useTranslation();
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<{ id: number; name: string } | null>(null);
    const [folderItems, setFolderItems] = useState<ItemDetailData[]>([]);
    const [itemsLoading, setItemsLoading] = useState(false);

    // 知识库目录数据 (用于弹窗列表数据)
    const { knowledgeFolders, loading: knowledgeLoading } = agentDashboardKnowledgeHooks.useKnowledgeFoldersData();

    const handleFolderClick = async (id: number, name: string) => {
      setSelectedFolder({ id, name });
      setDetailOpen(true);
      setItemsLoading(true);
      setFolderItems([]); // Clear previous items
      try {
        const res = await knowledgeGetFolderItems(id);
        setFolderItems(res.items.map(item => item.itemDetail[0]) || []);
      } catch (e) {
        console.error(e);
      } finally {
        setItemsLoading(false);
      }
    };

    // 保存添加
    const handleAdd = async (ids: number[]) => {
      await onAddKnowledgeFolders(ids);
    }

    // 保存移除
    const handleRemove = async (ids: number[]) => {
      await onRemoveKnowledgeFolders(ids);
    }

    // 更新知识库目录（添加/移除）
    const handleSave = async (ids: number[]) => {
      const folders = knowledge.map(item => item.folderId);
      // 需要移除的id
      const removeIds = folders.filter(item => !ids.includes(item));
      // 需要添加的id
      const addIds = ids.filter(item => !removeIds.includes(item));
      await onAddKnowledge?.({ removeIds, addIds });
    }

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/0" />
          <Dialog.Content
            className="
            fixed right-0 top-0 z-50 h-full w-[480px]
            bg-white flex flex-col
            data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full
            transition-transform duration-300
          "
          >
            {/* 头部 */}
            <div className="flex items-center justify-between my-6 px-6">
              <div
                className="flex items-center gap-4 text-[rgba(16,24,40,1)]"
              >
                <ModalCloseButton
                  icon={<ChevronLeft className="w-5 h-5" />}
                  onClick={() => onOpenChange(false)}
                />
                <div className="text-base font-medium">
                  {t("agent_creation.subscription_settings.knowledge")}
                </div>
              </div>
              <ModalCloseButton
                onClick={() => onOpenChange(false)}
              />
            </div>

            <div className="flex-1 overflow-auto px-6">
              <div
                className={cn("grid gap-x-3 gap-y-4", !knowledge?.length && "min-h-full")}
                style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
              >
                {knowledge?.length ? knowledge.map((m) => (
                  <button
                    key={m.id}
                    className="text-center"
                    onClick={() => handleFolderClick(m.folderId, m.folderName)}
                  >
                    <div className="mb-2 w-full aspect-[1] overflow-hidden flex items-center justify-center">
                      <Image src={knowledgeBg} className="w-full h-auto object-contain" alt={m.folderName} />
                    </div>
                    <TruncatedText className="max-w-[90px] text-sm text-[rgba(16,24,40,1)]" text={m.folderName} />
                  </button>
                )) : (
                  <div className='w-full flex-1 min-h-128 relative flex flex-col justify-center items-center col-span-full gap-2'>
                    <KnowledgeEmptyImage className='w-[120px] h-[103px]' />
                    <span className='text-sm text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.knowledge.detail_modal.empty_tips2")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 右下角移除/添加文件按钮 */}
            <div className="absolute right-6 bottom-6 flex gap-3 items-center">
              <KnowledgeChooseModal
                trigger={
                  <button className="flex items-center gap-1.5 px-7 py-2.5 rounded-[8px] bg-white text-sm  text-nowrap whitespace-nowrap text-[rgba(16,24,40,1)] font-semibold hover:bg-gray-50 transition-colors border border-[rgba(208,213,221,1)]">
                    <MinusCircleIcon className="w-4 h-4 text-[rgba(16,24,40,1)]" />
                    <span>{t("agent_creation.subscription_settings.remove")}</span>
                  </button>
                }
                title={t("agent_creation.subscription_settings.add_remove_modal.knowledge_remove_title")}
                confirmText={t("agent_creation.subscription_settings.add_remove_modal.remove")}
                knowledgeFolders={knowledge.map(item => ({
                  folderId: item.folderId,
                  folderName: item.folderName,
                  id: item.folderId,
                  createTime: item.createTime ? new Date(item.createTime).getTime() : 0,
                }))}
                loading={saveLoading}
                onConfirm={handleRemove}
              />
              <KnowledgeChooseModal
                trigger={
                  <button className="flex items-center gap-1.5 px-7 py-2.5 rounded-[8px] bg-[rgba(16,24,40,1)] text-sm text-white text-nowrap whitespace-nowrap font-semibold hover:bg-[rgba(24,39,64,1)]/95 transition-colors">
                    <PlusCircleIcon className="w-4 h-4 text-white" />
                    <span>{t("agent_creation.subscription_settings.add")}</span>
                  </button>
                }
                title={t("agent_creation.subscription_settings.add_remove_modal.knowledge_add_title")}
                knowledgeFolders={knowledgeFolders}
                disabledIds={knowledge.map(item => item.folderId)}
                loading={saveLoading}
                confirmText={t("agent_creation.subscription_settings.add_remove_modal.add")}
                onConfirm={handleAdd}
              />
            </div>
            <KnowledgeModalDetail
              open={detailOpen}
              onOpenChange={setDetailOpen}
              name={selectedFolder?.name}
              knowledgeFolderItems={folderItems}
              loading={itemsLoading}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  };
